import { useCallback, useEffect, useState } from "react";
import { apiErrorCode, apiErrorMessage } from "@/lib/api-error";
import { PASSWORD_RULE } from "@/lib/password";

export { PASSWORD_RULE, passwordProblem } from "@/lib/password";

/**
 * Words and timers shared by the pre-token screens: sign-in and
 * forgot-password.
 *
 * Every branch keys off the API's `error` code, never its status alone. Two
 * 401s from the same form can mean "wrong password" and "code expired", and
 * both deserve a sentence that points at the fix.
 */

type Step = "password" | "code" | "email";

export function authErrorMessage(error: unknown, step: Step): string {
  const code = apiErrorCode(error);

  switch (code) {
    // One code for wrong password, unknown email and a locked account (five
    // wrong tries, 15 minutes), by design. Nothing here may confirm whether an
    // account exists.
    case "INVALID_CREDENTIALS":
      return "Incorrect email or password.";
    case "INVALID_OTP":
      return "That code isn't right or has expired. Try again or send a new one.";
    case "ACCOUNT_SUSPENDED":
      return "Your account is suspended. Contact the owner.";
    case "OTP_COOLDOWN": {
      const wait = retryAfterSeconds(error);
      return wait
        ? `A code was just sent. You can ask for another in ${formatWait(wait)}.`
        : "A code was just sent. Wait a moment before asking for another.";
    }
    case "OTP_RESEND_LIMIT":
      return "Too many codes have been sent to this address. Try again later.";
    case "RATE_LIMITED":
      return "Too many attempts, try again later.";
    case "EMAIL_DELIVERY_FAILED":
      return "The email could not be sent. Try sending a new code.";
    case "WEAK_PASSWORD":
      return weakPasswordMessage(error);
  }

  const status =
    typeof error === "object" && error !== null && "status" in error
      ? (error as { status: unknown }).status
      : null;

  // A 401 without a recognised code: say the least that is still useful.
  if (status === 401) {
    return step === "password"
      ? "Incorrect email or password."
      : "That code isn't right or has expired. Try again or send a new one.";
  }
  if (status === "FETCH_ERROR")
    return "Can't reach the server. Check your connection and try again.";

  return apiErrorMessage(error, "Something went wrong. Try again.");
}

/** `details.retry_after_seconds`, when the API sent one. */
export function retryAfterSeconds(error: unknown): number | null {
  const data =
    typeof error === "object" && error !== null ? (error as { data?: unknown }).data : null;
  const details =
    typeof data === "object" && data !== null ? (data as { details?: unknown }).details : null;
  const seconds =
    typeof details === "object" && details !== null
      ? (details as { retry_after_seconds?: unknown }).retry_after_seconds
      : null;
  return typeof seconds === "number" && seconds > 0 ? Math.ceil(seconds) : null;
}

function weakPasswordMessage(error: unknown): string {
  const data =
    typeof error === "object" && error !== null ? (error as { data?: unknown }).data : null;
  const body = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  const details =
    typeof body.details === "object" && body.details !== null
      ? (body.details as { reason?: unknown })
      : {};
  if (typeof details.reason === "string") return `Password rejected: ${details.reason}.`;
  return typeof body.message === "string" ? body.message : PASSWORD_RULE;
}

function formatWait(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.ceil(seconds / 60);
  return `${String(minutes)} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * Seconds left until a resend is allowed, counting down to 0, and the function
 * that restarts it.
 *
 * Restarted with whatever the API said — a fresh challenge's
 * `resend_available_in`, or the `retry_after_seconds` of a cooldown refusal —
 * so the button reflects the server's clock rather than one the browser
 * guessed.
 */
export function useCountdown(): [secondsLeft: number, start: (seconds: number) => void] {
  const [endsAt, setEndsAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (endsAt === 0) return;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= endsAt) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [endsAt]);

  const start = useCallback((seconds: number) => {
    const current = Date.now();
    setNow(current);
    setEndsAt(seconds > 0 ? current + seconds * 1000 : 0);
  }, []);

  return [Math.max(0, Math.ceil((endsAt - now) / 1000)), start];
}
